/**
 * WebSocket client for token-by-token streaming of Avery's replies. Opt-in via
 * VITE_USE_WS=true; otherwise the store uses the REST path. The connection carries the
 * Cognito ID token + assessmentId as query params ($connect authorizer reads the token
 * from the query string). Frames: message_start / token / message_complete / error.
 */
import { WS_BASE } from './client';
import { getIdToken } from '../auth/cognito';

export const wsEnabled = import.meta.env.VITE_USE_WS === 'true' && Boolean(WS_BASE);

export interface TurnHandlers {
  onStart?: (stage: number) => void;
  onToken: (chunk: string) => void;
  onComplete: (info: { stage: number; stageAdvanced: boolean; done: boolean; budgetNotice?: string }) => void;
  onError: (message: string) => void;
}

export class AveryWs {
  private socket?: WebSocket;
  constructor(private readonly assessmentId: string) {}

  private url(): string {
    const token = getIdToken() ?? '';
    return `${WS_BASE}?token=${encodeURIComponent(token)}&assessmentId=${encodeURIComponent(this.assessmentId)}`;
  }

  /** Open the connection; resolves on open, rejects on error/timeout. */
  connect(timeoutMs = 8000): Promise<void> {
    return new Promise((resolve, reject) => {
      const s = new WebSocket(this.url());
      this.socket = s;
      const timer = window.setTimeout(() => {
        try { s.close(); } catch { /* noop */ }
        reject(new Error('ws connect timeout'));
      }, timeoutMs);
      s.onopen = () => { window.clearTimeout(timer); resolve(); };
      s.onerror = () => { window.clearTimeout(timer); reject(new Error('ws connection error')); };
    });
  }

  /** Send one turn and stream the reply via the handlers. */
  sendTurn(text: string, h: TurnHandlers): void {
    const s = this.socket;
    if (!s || s.readyState !== WebSocket.OPEN) {
      h.onError('not connected');
      return;
    }
    // Guard against the socket dropping mid-stream: without this, a close/error before
    // `message_complete` would leave the turn hanging forever (typing indicator stuck,
    // a half-streamed bubble). `settled` makes the terminal callback fire exactly once.
    let settled = false;
    const finish = () => {
      settled = true;
      s.onclose = null;
      s.onerror = null;
    };
    s.onmessage = (ev) => {
      let f: { type?: string; text?: string; stage?: number; stageAdvanced?: boolean; done?: boolean; budgetNotice?: string; message?: string };
      try { f = JSON.parse(String(ev.data)); } catch { return; }
      switch (f.type) {
        case 'message_start': h.onStart?.(f.stage ?? 0); break;
        case 'token': if (f.text) h.onToken(f.text); break;
        case 'message_complete':
          finish();
          h.onComplete({ stage: f.stage ?? 0, stageAdvanced: Boolean(f.stageAdvanced), done: Boolean(f.done), budgetNotice: f.budgetNotice });
          break;
        case 'error': finish(); h.onError(f.message ?? 'error'); break;
      }
    };
    s.onclose = () => { if (!settled) { settled = true; h.onError('connection lost'); } };
    s.onerror = () => { if (!settled) { settled = true; h.onError('connection lost'); } };
    s.send(JSON.stringify({ action: 'message', assessmentId: this.assessmentId, text }));
  }

  close(): void {
    try { this.socket?.close(); } catch { /* noop */ }
  }
}
